import { CTAGroup } from "@components/CTAGroup";
import LogosBlock, { LogosBlockProps } from "@components/LogosBlock/logosBlock";
import { getSanityImgUrl } from "@lib/sanity";
import { BlockContent } from "@lib/sanity-components";
import {
  IMAGE_DIMENSION_HERO_SECTION_LOGO_WIDTH,
  IMAGE_DIMENSION_HERO_SECTION_WIDTH,
} from "@lib/utils/imageDimensions";
import { ImageUrlBuilder } from "@sanity/image-url/lib/types/builder";
import {
  Heading,
  styled,
  css,
  Link as SourcefulLink,
  PrimaryButton,
  CSS,
} from "@sourceful/shared-components";
import {
  HeroLayout,
  Image,
  ImageLink,
  SanityHeroSection,
  SanityTextAlign,
} from "@sourceful/shared-types";
import Link from "next/link";

export interface HeroProps {
  section: SanityHeroSection;
  imageGetter?: (
    assetRef: string,
    transforms?: ((baseTransforms: ImageUrlBuilder) => ImageUrlBuilder) | undefined
  ) => string;
}

const GlobalHeroWrapper = styled("div", {
  width: "100%",
  padding: "clamp(20px, 3vw, 60px)",
});

const HeroWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  width: "100%",
  justifyContent: "center",
});

const SecondaryWrapper = styled("div", {
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  flexWrap: "wrap",
  flexDirection: "column",
  "@md": {
    flexDirection: "row",
  },
});

const ContentWrapper = styled("div", {
  paddingTop: "1rem",
  paddingBottom: "2rem",
  width: "100%",

  "@md": {
    flex: "1 1 50%",
  },

  variants: {
    layout: {
      textPanelLeft: {
        "@md": {
          order: 1,
        },
        "@xl": {
          paddingRight: "4rem",
        },
      },
      textPanelRight: {
        "@md": {
          order: 2,
        },
        "@xl": {
          paddingLeft: "4rem",
        },
      },
    },
  },
});

const Header = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  textAlign: "left",
});

const VideoWrapper = styled("div", {
  position: "relative",
  width: "100%",
  height: "16rem",
  "@sm": {
    height: "20rem",
  },

  "@md": {
    height: "24rem",
    flex: "1 1 50%",
  },

  variants: {
    layout: {
      textPanelLeft: {
        "@md": {
          order: 2,
        },
      },
      textPanelRight: {
        "@md": {
          order: 1,
        },
      },
    },
  },
});

const ImageWrapper = styled("div", {
  position: "relative",
  width: "100%",
  height: "16rem",
  "@sm": {
    height: "20rem",
  },

  "@md": {
    height: "24rem",
    flex: "1 1 50%",
  },

  variants: {
    layout: {
      textPanelLeft: {
        "@md": {
          order: 2,
        },
      },
      textPanelRight: {
        "@md": {
          order: 1,
        },
      },
    },
  },
});

const Video = styled("video", {
  objectFit: "cover",
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: "100%",
  height: "100%",
});

const StyledImage = styled("img", {
  objectFit: "cover",
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: "100%",
  height: "100%",
});

const ctaGroupStyles = css({
  //marginBlock: "clamp(30px, 5vw, 60px)",
  marginBlock: "$block_s1",
  justifyContent: "flex-start",

  "@lg": {
    marginBlock: "$block_m1",
  },
});

const textAlignToJustifyContent = (textAlign: SanityTextAlign | undefined) => {
  switch (textAlign) {
    case "left":
      return "flex-start";
    case "right":
      return "flex-end";
    case "center":
      return "center";
    default:
      return "flex-start";
  }
};

const HeroSection = ({ section, imageGetter = getSanityImgUrl }: HeroProps) => {
  const {
    _key,
    layout = HeroLayout.TEXT_PANEL_LEFT,
    logoWall,
    title,
    subtitle,
    primaryCTA,
    secondaryCTA,
    linkCTA,
    primaryVideo,
    primaryImage,
    backgroundImage,
    bgImageSize = "cover",
    bgImagePosition = "center",
    fgImageSize = "cover",
    fgImagePosition = "center",
    constrainContent = true,
    backgroundColour,
    textColour,
    breakoutTitle = false,
    textAlign,
    titleAsH1 = false,
  } = section;

  const formatSanityLogos = (logos: (Image | ImageLink)[]): LogosBlockProps["logos"] => {
    return logos.map(logo => {
      const logoImage = logo._type === "logoImage" ? logo : (logo as ImageLink).image;

      return {
        image: {
          url: imageGetter((logoImage as Image)?.asset?._ref, base =>
            base.width(IMAGE_DIMENSION_HERO_SECTION_LOGO_WIDTH)
          ),
          alt: (logoImage as Image)?.asset?.altText || "",
          link: (logo as ImageLink)?.link || null,
        },
      };
    });
  };

  let globalHeroWrapperCss: CSS = {
    backgroundSize: bgImageSize,
    backgroundPosition: bgImagePosition,
    backgroundRepeat: "no-repeat",
    ...(backgroundColour && { backgroundColour: backgroundColour.value }),
    ...(textColour && { color: textColour.value }),
    ...(backgroundImage && { backgroundImage: `url(${backgroundImage})` }),
  };

  return (
    <GlobalHeroWrapper css={globalHeroWrapperCss}>
      <HeroWrapper>
        <SecondaryWrapper css={{ maxWidth: constrainContent ? "1200px" : undefined }}>
          {breakoutTitle && (
            <Heading
              level={titleAsH1 ? 1 : 2}
              css={{ width: "100%", textAlign: textAlign ? textAlign : "center", mbe: "$block_m3" }}
            >
              {title}
            </Heading>
          )}
          <ContentWrapper layout={layout} css={{ pbs: breakoutTitle ? "0" : "$block_s2" }}>
            <Header css={{ textAlign: textAlign }}>
              {!breakoutTitle && <Heading level={titleAsH1 ? 1 : 2}>{title}</Heading>}
              {subtitle && <BlockContent value={subtitle} />}
            </Header>

            {primaryCTA && primaryCTA?.url && (
              <CTAGroup
                className={ctaGroupStyles({
                  css: { justifyContent: textAlignToJustifyContent(textAlign) },
                })}
              >
                <Link passHref href={primaryCTA.url} legacyBehavior>
                  <PrimaryButton as="a" compact tabIndex={0}>
                    {primaryCTA?.title}
                  </PrimaryButton>
                </Link>
                {secondaryCTA && secondaryCTA?.title && secondaryCTA?.url && (
                  <Link passHref href={secondaryCTA.url} legacyBehavior>
                    <PrimaryButton as="a" appearance="light" compact tabIndex={0}>
                      {secondaryCTA.title}
                    </PrimaryButton>
                  </Link>
                )}
                {linkCTA && linkCTA?.title && linkCTA?.url && (
                  <Link passHref href={linkCTA.url} legacyBehavior>
                    <SourcefulLink href={linkCTA.url}>{linkCTA.title}</SourcefulLink>
                  </Link>
                )}
              </CTAGroup>
            )}

            {logoWall && (
              <LogosBlock
                keyPrefix={_key}
                title={logoWall.title}
                logos={formatSanityLogos(logoWall?.logos || [])}
                isRightAligned={textAlign === "right"}
              />
            )}
          </ContentWrapper>
          {primaryVideo && primaryVideo.length > 0 ? (
            <VideoWrapper layout={layout}>
              <Video
                data-cy="hero-video"
                css={{
                  objectFit: fgImageSize,
                  objectPosition: fgImagePosition,
                  filter: "brightness(102%)",
                }}
                autoPlay
                playsInline
                muted
                loop
                src={primaryVideo || ""}
              >
                {" "}
                <StyledImage
                  css={{
                    objectFit: fgImageSize,
                    objectPosition: fgImagePosition,
                  }}
                  src={imageGetter(primaryImage?.asset?._ref, base =>
                    base.width(IMAGE_DIMENSION_HERO_SECTION_WIDTH)
                  )}
                  alt={primaryImage?.asset.altText}
                  loading="lazy"
                />
              </Video>
            </VideoWrapper>
          ) : (
            <ImageWrapper layout={layout}>
              <StyledImage
                data-cy="hero-image"
                css={{
                  objectFit: fgImageSize,
                  objectPosition: fgImagePosition,
                }}
                src={imageGetter(primaryImage?.asset?._ref, base =>
                  base.width(IMAGE_DIMENSION_HERO_SECTION_WIDTH)
                )}
                alt={primaryImage?.asset.altText}
              />
            </ImageWrapper>
          )}
        </SecondaryWrapper>
      </HeroWrapper>
    </GlobalHeroWrapper>
  );
};

export default HeroSection;
